import { useMutation } from '@apollo/client';
import type {
  InsertApprovalMutation,
  InsertApprovalMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertApprovalDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useInsertApprovalTRPC } from './useInsertApprovalTRPC';

export const useInsertApproval = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [insertApprovalGraphQL, graphqlState] = useMutation(
    InsertApprovalDocument,
    {
      update: (cache) => evictField(cache, 'approval'),
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertApprovalTRPC();

  const insertApproval = async (
    approval: InsertApprovalMutationVariables['approval']
  ): Promise<InsertApprovalMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertApproval(approval);
    }

    const result = await insertApprovalGraphQL({
      variables: { approval },
    });
    if (!result.data) {
      throw new Error('Failed to insert approval');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertApproval,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertApproval,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
